import { isValid, parseISO, subDays } from "date-fns";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import type { DateRange } from "@/components/ui/v2/date-range-picker";
import { URL_PARAMS } from "@/constants/urlParams";

export const CHANGE_TYPE_PARAM = "changeType";

function parseDate(value: string | null, fallback: Date): Date {
  if (!value) return fallback;
  const date = parseISO(value);
  return isValid(date) ? date : fallback;
}

export function useHistoryUrlFilters({ defaultDays }: { defaultDays: number }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const changeTypes = searchParams.getAll(CHANGE_TYPE_PARAM);
  const fromParam = searchParams.get(URL_PARAMS.DATE_FROM);
  const toParam = searchParams.get(URL_PARAMS.DATE_TO);

  const dateRange = useMemo<DateRange>(() => {
    const now = new Date();
    return { from: parseDate(fromParam, subDays(now, defaultDays)), to: parseDate(toParam, now) };
  }, [fromParam, toParam, defaultDays]);

  const update = (change: (next: URLSearchParams) => void) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        change(next);
        return next;
      },
      { replace: true },
    );

  const onChangeTypesChange = (types: string[]) =>
    update((next) => {
      next.delete(CHANGE_TYPE_PARAM);
      types.forEach((type) => next.append(CHANGE_TYPE_PARAM, type));
    });

  const onDateRangeChange = (range: DateRange) =>
    update((next) => {
      if (range.from) next.set(URL_PARAMS.DATE_FROM, range.from.toISOString());
      else next.delete(URL_PARAMS.DATE_FROM);
      if (range.to) next.set(URL_PARAMS.DATE_TO, range.to.toISOString());
      else next.delete(URL_PARAMS.DATE_TO);
    });

  return { dateRange, hasEndDate: !!toParam, changeTypes, onDateRangeChange, onChangeTypesChange };
}
